import { ChangeEvent, FC, useCallback, useMemo } from "react";
import {
  Draggable,
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from "react-beautiful-dnd";

import { IIconBandSetting, IEditItem } from "Bands/types";
import { IconSelect, InputField } from "shared/components";
import { DeleteIcon, DragAndDropButton, DeleteIconProps } from "./index";
import { StyledRow, StyledCell } from "./styles";
import { FieldError } from "react-hook-form";

interface EditRowProps extends DeleteIconProps {
  item: IEditItem;
  hasIcon?: boolean;
  hasDragAndDrop?: boolean;
  index: number;
  canDeleteItem?: boolean;
  error?: FieldError | null;
  updateItem: (item: IEditItem) => void;
}

export const EditRow: FC<EditRowProps> = ({
  item,
  hasDragAndDrop,
  hasIcon,
  index,
  canDeleteItem,
  error,
  updateItem,
  handleDelete,
}) => {
  const { id, label } = useMemo(() => item, [item]);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      updateItem({ ...item, label: value });
    },
    [updateItem, item]
  );

  const onIconChange = useCallback(
    (value: string) => {
      updateItem({ ...item, icon: value });
    },
    [updateItem, item]
  );

  const renderInner = useCallback(
    (
      ref?: (instance: HTMLDivElement | null) => void,
      draggableProps?: DraggableProvidedDraggableProps,
      dragHandleProps?: DraggableProvidedDragHandleProps
    ) => (
      <StyledRow
        hasDragAndDrop={hasDragAndDrop}
        hasIcon={hasIcon}
        ref={ref}
        {...(draggableProps || {})}
      >
        {hasDragAndDrop && (
          <StyledCell {...(dragHandleProps || {})}>
            <DragAndDropButton />
          </StyledCell>
        )}
        {hasIcon && (
          <StyledCell>
            <IconSelect value={(item as IIconBandSetting).icon} setValue={onIconChange} />
          </StyledCell>
        )}
        <StyledCell>
          <InputField
            name={id}
            value={label}
            onChange={onInputChange}
            error={error}
            maxLength={100}
          />
        </StyledCell>
        <StyledCell>{canDeleteItem && <DeleteIcon handleDelete={handleDelete} />}</StyledCell>
      </StyledRow>
    ),
    [
      onInputChange,
      hasIcon,
      id,
      label,
      item,
      onIconChange,
      handleDelete,
      hasDragAndDrop,
      canDeleteItem,
      error,
    ]
  );

  if (hasDragAndDrop) {
    return (
      <Draggable key={id} draggableId={id} index={index}>
        {provided =>
          renderInner(provided.innerRef, provided.draggableProps, provided.dragHandleProps)
        }
      </Draggable>
    );
  }

  return renderInner();
};
